/***************************************************************************************
 * FileName      : quad-text.ts
 * Description   : 4개 꼭짓점 좌표 기반 텍스트 배치 타입 정의
 *                 - 사각형(Quad) 꼭짓점 좌표, 텍스트 스타일, 배치 설정, 노드 관리
 *                 - 앵커 기반 텍스트 라벨 배치 (text.json)
 *                 - 앵커 위치 → 4개 꼭짓점 변환 유틸리티 (computeLabelCorners)
 * Author        : (주)한국공학기술연구원 박은정
 * Created Date  : 2026.02.12
 * Modified Date :
 * Reference     : matterport_mvp1/src/09-text-quad-placement/src/types/quad-text.ts
 ***************************************************************************************/

// 2026.02.12 : 박은정 : 공통 3D 좌표 타입은 quad-image.ts에서 재사용
import { Vector3, QuadCorners } from './quad-image';

// 2026.02.12 : 박은정 : quad-image.ts의 공통 타입 re-export (소비자 편의)
export type { Vector3, QuadCorners };

// ============================================================================
// 텍스트 스타일 설정
// ============================================================================

/**
 * 2026.02.12 : 박은정 : 텍스트 정렬 방식
 */
export type TextAlign = 'left' | 'center' | 'right';

/**
 * 2026.02.12 : 박은정 : 텍스트 스타일 설정
 */
export interface TextStyle {
  /** 폰트 크기 (px) */
  fontSize: number;
  /** 폰트 패밀리 */
  fontFamily: string;
  /** 폰트 굵기 */
  fontWeight: string;
  /** 텍스트 색상 */
  textColor: string;
  /** 배경 색상 */
  backgroundColor: string;
  /** 텍스트 정렬 */
  textAlign: TextAlign;
  /** 줄 간격 배수 (1.0 = 기본) */
  lineHeight: number;
  /** 패딩 (px) */
  padding: number;
}

// ============================================================================
// 텍스트 배치 설정
// ============================================================================

/**
 * 2026.02.12 : 박은정 : 4개 꼭짓점 기반 텍스트 배치 설정
 */
export interface QuadTextPlacement {
  /** 고유 ID */
  id: string;
  /** 텍스트 표시명 */
  name: string;
  /** 표시할 텍스트 내용 */
  text: string;
  /** 텍스트 스타일 */
  textStyle: TextStyle;
  /** 4개 꼭짓점 좌표 */
  corners: QuadCorners;
  /** 투명도 (0.0 ~ 1.0) */
  opacity: number;
  /** 표시 여부 */
  visible: boolean;
  /** 텍스처 해상도 (픽셀) */
  textureSize?: { w: number; h: number };
  /** 2026.02.12 : 박은정 : 테두리 표시 여부 (기본 true) */
  showBorder?: boolean;
}

// ============================================================================
// 텍스트 노드 관리
// ============================================================================

/** 텍스트 노드 관리 정보 */
export interface QuadTextNodeInfo {
  /** 텍스트 ID */
  id: string;
  /** 배치 설정 */
  placement: QuadTextPlacement;
  /** Matterport Scene Node */
  node: any;
  /** mp.quadTextPlane 컴포넌트 참조 */
  component: any;
}

// ============================================================================
// 상수 정의
// ============================================================================

/** 기본 텍스트 스타일 */
export const DEFAULT_TEXT_STYLE: TextStyle = {
  fontSize: 48,
  fontFamily: 'Arial, sans-serif',
  fontWeight: 'bold',
  textColor: '#ffffff',
  backgroundColor: 'rgba(15, 23, 42, 0.85)',
  textAlign: 'center',
  lineHeight: 1.4,
  padding: 40,
};

// ============================================================================
// 2026.02.12 : 박은정 : 앵커 기반 텍스트 라벨 타입 (text.json)
// ============================================================================

/**
 * 2026.02.12 : 박은정 : text.json 항목 구조
 * - 저장 시 anchorPosition = 원본 앵커 + 오프셋 (합산 저장)
 * - offset, width, height는 런타임 전용 (JSON에 저장하지 않음)
 */
export interface TextLabelJsonEntry {
  id: string;
  /** 원본 라벨명 (변경 불가) */
  label: string;
  /** 2026.02.12 : 박은정 : 화면에 표시할 텍스트 (편집 가능, 없으면 label 사용) */
  text?: string;
  /** 2026.04.23 : 박은정 : UV 항목은 anchorPosition 없이 저장됨 */
  anchorPosition?: Vector3;
  /** 회전 각도 (도, 기본값과 다를 때만 저장) */
  rotation?: Vector3;
  /** 폰트 크기 (기본값과 다를 때만 저장) */
  fontSize?: number;
  /** 텍스트 색상 (기본값과 다를 때만 저장) */
  textColor?: string;
  /** 배경 색상 (기본값과 다를 때만 저장) */
  backgroundColor?: string;
  // ============================================================
  // 2026.04.23 : 박은정 : UV 기반 배치 (monitor + u/v + uWidth/uHeight)
  //   - 모니터 4꼭짓점을 bilinear 보간해 라벨 중심 좌표 계산
  //   - uWidth/uHeight: 모니터 대비 라벨 크기 비율 (0~1)
  // ============================================================
  /** 소속 모니터 ID (예: monitor-01) */
  monitor?: string;
  /** 모니터 UV 중심 (0~1, 좌상단 기준) */
  u?: number;
  v?: number;
  /** 라벨 UV 크기 (모니터 대비 비율, 0~1) */
  uWidth?: number;
  uHeight?: number;
}

/**
 * 2026.02.12 : 박은정 : 런타임 텍스트 라벨 상태 (UI 편집용)
 */
export interface TextLabelState {
  /** JSON 원본 ID */
  id: string;
  /** 원본 라벨명 (변경 불가, 식별용) */
  label: string;
  /** 2026.02.12 : 박은정 : 화면에 표시할 텍스트 (편집 가능) */
  text: string;
  /** 앵커 위치 (원본, 변경 불가) */
  anchorPosition: Vector3;
  /** 위치 오프셋 (사용자 조정) */
  offset: Vector3;
  /** 회전 각도 (도, x=pitch y=yaw z=roll) */
  rotation: Vector3;
  /** 라벨 물리 너비 (m) */
  width: number;
  /** 라벨 물리 높이 (m) */
  height: number;
  /** 폰트 크기 (캔버스 px) */
  fontSize: number;
  /** 텍스트 색상 */
  textColor: string;
  /** 배경 색상 */
  backgroundColor: string;
  /** 배치 여부 */
  isPlaced: boolean;
}

// ============================================================================
// 2026.02.12 : 박은정 : 텍스트 라벨 기본값
// ============================================================================

/** 라벨 기본 너비 (m) */
export const DEFAULT_LABEL_WIDTH = 0.30;
/** 라벨 기본 높이 (m) */
export const DEFAULT_LABEL_HEIGHT = 0.04;
/** 라벨 기본 폰트 크기 (캔버스 px) */
export const DEFAULT_LABEL_FONT_SIZE = 36;
/** 라벨 기본 텍스트 색상 */
export const DEFAULT_LABEL_TEXT_COLOR = '#000000';
/** 라벨 기본 배경 색상 (투명) */
export const DEFAULT_LABEL_BG_COLOR = 'rgba(0,0,0,0)';
/** 라벨 텍스처 크기 */
export const DEFAULT_LABEL_TEXTURE = { w: 512, h: 128 };

// 2026.04.23 : 박은정 : UV 기반 라벨 기본 크기 (모니터 대비 비율)
export const DEFAULT_UV_WIDTH = 0.11;
export const DEFAULT_UV_HEIGHT = 0.045;

/**
 * 2026.04.23 : 박은정 : 모니터 4꼭짓점 + UV(u, v) + 라벨 크기(uWidth, uHeight)로
 *   라벨 4꼭짓점을 계산.
 * - (u, v)는 라벨 중심의 모니터 UV 좌표 (0,0 = 좌상단, 1,1 = 우하단)
 * - uWidth/uHeight는 모니터 대비 라벨 가로/세로 비율
 * - 모니터 평면 위에 라벨을 정확히 올림 → 평행시차 없음
 */
export function uvToLabelCorners(
  monitorCorners: QuadCorners,
  u: number,
  v: number,
  uWidth: number,
  uHeight: number,
): QuadCorners {
  const { topLeft: TL, topRight: TR, bottomLeft: BL, bottomRight: BR } = monitorCorners;

  // bilinear 보간 헬퍼
  const lerp = (a: Vector3, b: Vector3, t: number): Vector3 => ({
    x: a.x + (b.x - a.x) * t,
    y: a.y + (b.y - a.y) * t,
    z: a.z + (b.z - a.z) * t,
  });
  const bilinear = (uu: number, vv: number): Vector3 => {
    const top = lerp(TL, TR, uu);
    const bot = lerp(BL, BR, uu);
    return lerp(top, bot, vv);
  };

  // 중심 UV 기준 라벨 4모서리 UV
  const u0 = u - uWidth / 2;
  const u1 = u + uWidth / 2;
  const v0 = v - uHeight / 2;
  const v1 = v + uHeight / 2;

  return {
    topLeft: bilinear(u0, v0),
    topRight: bilinear(u1, v0),
    bottomLeft: bilinear(u0, v1),
    bottomRight: bilinear(u1, v1),
  };
}

/**
 * 2026.04.23 : 박은정 : 라벨을 모니터 평면 법선 방향(카메라 쪽)으로 push만큼 이동.
 * - Matterport 스캔 메시가 모니터보다 앞에 있을 때 가려지는 현상 방지
 */
export function pushCornersAlongPlaneNormal(
  labelCorners: QuadCorners,
  monitorCorners: QuadCorners,
  push: number,
): QuadCorners {
  const { topLeft: TL, topRight: TR, bottomLeft: BL } = monitorCorners;
  const ex = TR.x - TL.x, ey = TR.y - TL.y, ez = TR.z - TL.z;
  const fx = BL.x - TL.x, fy = BL.y - TL.y, fz = BL.z - TL.z;
  let nx = ey * fz - ez * fy;
  let ny = ez * fx - ex * fz;
  let nz = ex * fy - ey * fx;
  const nLen = Math.hypot(nx, ny, nz);
  if (nLen < 1e-9) return labelCorners;
  nx /= nLen; ny /= nLen; nz /= nLen;
  if (nz < 0) { nx = -nx; ny = -ny; nz = -nz; }
  const dx = push * nx, dy = push * ny, dz = push * nz;
  return {
    topLeft:     { x: labelCorners.topLeft.x + dx,     y: labelCorners.topLeft.y + dy,     z: labelCorners.topLeft.z + dz },
    topRight:    { x: labelCorners.topRight.x + dx,    y: labelCorners.topRight.y + dy,    z: labelCorners.topRight.z + dz },
    bottomLeft:  { x: labelCorners.bottomLeft.x + dx,  y: labelCorners.bottomLeft.y + dy,  z: labelCorners.bottomLeft.z + dz },
    bottomRight: { x: labelCorners.bottomRight.x + dx, y: labelCorners.bottomRight.y + dy, z: labelCorners.bottomRight.z + dz },
  };
}

// ============================================================================
// 2026.02.12 : 박은정 : 앵커 위치 → 4개 꼭짓점 변환 유틸리티
// ============================================================================

/**
 * 2026.02.12 : 박은정 : 앵커 위치 + 크기 + 회전으로 4개 꼭짓점 계산
 *
 * 기본 방향: 평면이 +Z 방향을 향함 (뷰어 방향)
 * 회전 순서: ZYX (roll → yaw → pitch)
 *
 * @param anchor 앵커 위치 (원본)
 * @param offset 위치 오프셋
 * @param width 라벨 너비 (m)
 * @param height 라벨 높이 (m)
 * @param rotation 회전 (도, x=pitch y=yaw z=roll)
 * @returns 4개 꼭짓점 좌표
 */
export function computeLabelCorners(
  anchor: Vector3,
  offset: Vector3,
  width: number,
  height: number,
  rotation: Vector3,
): QuadCorners {
  // 2026.02.12 : 박은정 : 최종 중심점 = 앵커 + 오프셋
  const cx = anchor.x + offset.x;
  const cy = anchor.y + offset.y;
  const cz = anchor.z + offset.z;

  const hw = width / 2;
  const hh = height / 2;

  // 2026.02.12 : 박은정 : 라디안 변환
  const rx = (rotation.x * Math.PI) / 180;
  const ry = (rotation.y * Math.PI) / 180;
  const rz = (rotation.z * Math.PI) / 180;

  const cosX = Math.cos(rx), sinX = Math.sin(rx);
  const cosY = Math.cos(ry), sinY = Math.sin(ry);
  const cosZ = Math.cos(rz), sinZ = Math.sin(rz);

  // 2026.02.12 : 박은정 : 회전 행렬 (ZYX 순서) → right / up 벡터 계산
  const rightX = cosY * cosZ;
  const rightY = cosX * sinZ + sinX * sinY * cosZ;
  const rightZ = sinX * sinZ - cosX * sinY * cosZ;

  const upX = -cosY * sinZ;
  const upY = cosX * cosZ - sinX * sinY * sinZ;
  const upZ = sinX * cosZ + cosX * sinY * sinZ;

  // 2026.02.12 : 박은정 : 4개 꼭짓점 = 중심 ± (hw * right) ± (hh * up)
  return {
    topLeft: {
      x: cx + (-hw) * rightX + hh * upX,
      y: cy + (-hw) * rightY + hh * upY,
      z: cz + (-hw) * rightZ + hh * upZ,
    },
    topRight: {
      x: cx + hw * rightX + hh * upX,
      y: cy + hw * rightY + hh * upY,
      z: cz + hw * rightZ + hh * upZ,
    },
    bottomLeft: {
      x: cx + (-hw) * rightX + (-hh) * upX,
      y: cy + (-hw) * rightY + (-hh) * upY,
      z: cz + (-hw) * rightZ + (-hh) * upZ,
    },
    bottomRight: {
      x: cx + hw * rightX + (-hh) * upX,
      y: cy + hw * rightY + (-hh) * upY,
      z: cz + hw * rightZ + (-hh) * upZ,
    },
  };
}

